import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Copy,
  Check,
  ShieldCheck,
  Calendar,
  Lock,
  Users,
  RefreshCw,
  Share2,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import apiClient from "@/api/apiClient";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

interface OrganizationStatus {
  hasConsented: boolean;
  status: string;
  consentedAt?: string;
  consentedBy?: string;
  consentedByName?: string;
  azureTenantId?: string;
  organizationDomain?: string;
  adminConsentUrl?: string;
  directLink?: string;
  clientId?: string;
}

export const MicrosoftCalendarIntegration: React.FC = () => {
  const { isDark } = useDashboardTheme();
  const [status, setStatus] = useState<OrganizationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Fetch status on load
  const loadStatus = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/v1/calendar/admin/organization-status");
      if (res.data?.status && res.data?.data) {
        setStatus(res.data.data);
      }
    } catch (err: any) {
      console.error("Failed to load organization calendar status:", err);
      toast.error(err.response?.data?.message || "Failed to load Microsoft 365 status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();

    // Check for callback parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const consent = urlParams.get("consent");
    const errorMsg = urlParams.get("message") || urlParams.get("error");

    if (consent === "success") {
      toast.success("Microsoft 365 organization authorization successful! All company users can now access calendar features.");
      window.history.replaceState({}, document.title, window.location.pathname + "?tab=calendar");
    } else if (consent === "error" || consent === "cancelled") {
      toast.error(`Microsoft 365 authorization: ${errorMsg || "Consent was cancelled or denied"}`);
      window.history.replaceState({}, document.title, window.location.pathname + "?tab=calendar");
    }
  }, []);

  const handleCopyLink = () => {
    if (status?.hasConsented) {
      toast.info("This company is already connected. Authorization link has been single-use expired.");
      return;
    }
    const link = status?.directLink || status?.adminConsentUrl;
    if (!link) return;

    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Shareable IT Admin link copied to clipboard!");
    setTimeout(() => setCopied(false), 3000);
  };

  const handleAuthorizeClick = () => {
    if (status?.hasConsented) {
      toast.info("Microsoft 365 integration is already active for this organization.");
      return;
    }
    if (status?.adminConsentUrl) {
      window.location.href = status.adminConsentUrl;
    } else {
      toast.info("Preparing Microsoft authorization link...");
      loadStatus();
    }
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center p-12">
        <CircularProgress size={36} sx={{ color: "#a855f7" }} />
      </Box>
    );
  }

  const isConsented = Boolean(status?.hasConsented);
  const directLink = isConsented
    ? "Single-use authorization link has expired (Organization is connected)"
    : status?.directLink || status?.adminConsentUrl || "";

  return (
    <Box className="space-y-6">
      {/* Header */}
      <Box className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <Box>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shadow-sm">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Microsoft 365 & Calendar Integration
              </h2>
              <p className={`text-xs ${isDark ? "text-white/70" : "text-gray-500"} mt-0.5`}>
                Authorize Pitchster for your entire company so team members can connect Outlook and Teams calendars without individual admin approval blocks.
              </p>
            </div>
          </div>
        </Box>

        <button
          onClick={loadStatus}
          disabled={loading}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
            isDark
              ? "bg-[#1f222e] border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
              : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh Status
        </button>
      </Box>

      {/* Main Status Banner Card */}
      <div
        className={`rounded-2xl border transition-all p-6 ${
          isDark
            ? isConsented
              ? "bg-gradient-to-br from-[#0c231a] via-[#10241b] to-[#121820] border-emerald-500/30 text-white shadow-lg shadow-emerald-950/20"
              : "bg-gradient-to-br from-[#26170d] via-[#1f1614] to-[#16141e] border-amber-500/30 text-white shadow-lg shadow-amber-950/20"
            : isConsented
            ? "bg-emerald-50/90 border-emerald-200 text-gray-900 shadow-sm"
            : "bg-amber-50/90 border-amber-200 text-gray-900 shadow-sm"
        }`}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div
              className={`p-3 rounded-2xl shrink-0 ${
                isConsented
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                  : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
              }`}
            >
              {isConsented ? (
                <CheckCircle2 className="w-8 h-8" />
              ) : (
                <AlertTriangle className="w-8 h-8" />
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                    isConsented
                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                      : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isConsented ? "bg-emerald-400 animate-pulse" : "bg-amber-400"
                    }`}
                  />
                  {isConsented ? "Organization Access Active & Connected" : "Admin Consent Required"}
                </span>

                {status?.organizationDomain && (
                  <span className={`text-xs px-2.5 py-0.5 rounded-md font-mono border ${
                    isDark
                      ? "bg-white/10 text-white/80 border-white/10"
                      : "bg-gray-200 text-gray-700 border-gray-300"
                  }`}>
                    @{status.organizationDomain}
                  </span>
                )}
              </div>

              <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isConsented
                  ? "Pitchster is authorized for your entire company"
                  : "Authorize Pitchster for your Microsoft 365 organization"}
              </h3>

              <p className={`text-xs max-w-2xl leading-relaxed ${isDark ? "text-white/70" : "text-gray-600"}`}>
                {isConsented
                  ? "All company employees can now connect their Microsoft Outlook and Teams accounts directly in Pitchster without encountering the corporate 'Need admin approval' screen."
                  : "Corporate Microsoft 365 policies block standard users from linking third-party apps until an IT administrator gives tenant-level consent. Grant consent once below or share the link with your IT administrator."}
              </p>

              {isConsented && (
                <div className={`pt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs ${isDark ? "text-white/60" : "text-gray-600"}`}>
                  {status?.azureTenantId && (
                    <div>
                      <span className={isDark ? "text-white/40" : "text-gray-400"}>Microsoft Tenant ID: </span>
                      <span className={`font-mono ${isDark ? "text-white/80" : "text-gray-800"}`}>{status.azureTenantId}</span>
                    </div>
                  )}
                  {status?.consentedAt && (
                    <div>
                      <span className={isDark ? "text-white/40" : "text-gray-400"}>Authorized On: </span>
                      <span className={isDark ? "text-white/80" : "text-gray-800"}>
                        {new Date(status.consentedAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                  {status?.consentedByName && (
                    <div>
                      <span className={isDark ? "text-white/40" : "text-gray-400"}>Approved By: </span>
                      <span className={isDark ? "text-white/80" : "text-gray-800"}>{status.consentedByName}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Top Action Status Badge */}
          <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 shrink-0">
            {isConsented ? (
              <div
                className={`flex items-center justify-center gap-2 px-4 py-2.5 font-semibold text-xs rounded-xl border ${
                  isDark
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                    : "bg-emerald-50 border-emerald-200 text-emerald-700"
                }`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Connected to Microsoft 365</span>
              </div>
            ) : (
              <button
                onClick={handleAuthorizeClick}
                className="flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold text-xs rounded-xl shadow-lg shadow-purple-500/25 transition cursor-pointer"
              >
                <ExternalLink className="w-4 h-4" />
                Authorize Microsoft 365 (1-Click)
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Action Options Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Option 1: For Microsoft Admins */}
        <div
          className={`rounded-2xl border transition-all p-6 flex flex-col justify-between h-full space-y-4 ${
            isDark
              ? "bg-[#16151f] border-[#2e2c3b] text-white shadow-md shadow-black/20"
              : "bg-white border-gray-200 text-gray-900 shadow-sm"
          }`}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Option 1: I am a Microsoft 365 Administrator
                </h4>
                <p className="text-xs text-purple-400 font-medium">
                  {isConsented ? "Organization Authorized" : "Instant 1-Click Consent"}
                </p>
              </div>
            </div>

            <p className={`text-xs ${isDark ? "text-white/70" : "text-gray-600"} leading-relaxed`}>
              {isConsented
                ? "Your organization's administrator has already granted company-wide consent for Pitchster in Microsoft Entra ID."
                : "If your account has Global Administrator or Privileged Role Administrator rights in your company's Microsoft Entra ID (Azure AD), you can authorize Pitchster immediately."}
            </p>

            <div
              className={`p-3.5 rounded-xl text-xs space-y-2 border ${
                isDark
                  ? "bg-[#100f17] border-[#2e2c3b] text-white/80"
                  : "bg-gray-50 border-gray-200 text-gray-700"
              }`}
            >
              <div className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Official Microsoft Entra ID authorization verified</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Tenant-wide permissions active for all users</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Single-use authorization secured</span>
              </div>
            </div>
          </div>

          {isConsented ? (
            <div
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-xs border ${
                isDark
                  ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300"
                  : "bg-emerald-50 border-emerald-200 text-emerald-800"
              }`}
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Organization Authorization Completed</span>
            </div>
          ) : (
            <button
              onClick={handleAuthorizeClick}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold text-xs rounded-xl shadow-md transition cursor-pointer"
            >
              <ExternalLink className="w-4 h-4" />
              Authorize Microsoft 365 (1-Click)
            </button>
          )}
        </div>

        {/* Option 2: Shareable Link for IT Admin */}
        <div
          className={`rounded-2xl border transition-all p-6 flex flex-col justify-between h-full space-y-4 ${
            isDark
              ? "bg-[#16151f] border-[#2e2c3b] text-white shadow-md shadow-black/20"
              : "bg-white border-gray-200 text-gray-900 shadow-sm"
          }`}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                <Share2 className="w-5 h-5" />
              </div>
              <div>
                <h4 className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Option 2: Share with Your IT Department
                </h4>
                <p className="text-xs text-blue-400 font-medium">
                  {isConsented ? "Link Completed & Expired" : "For Managers & Team Leaders"}
                </p>
              </div>
            </div>

            <p className={`text-xs ${isDark ? "text-white/70" : "text-gray-600"} leading-relaxed`}>
              {isConsented
                ? "The single-use authorization link has expired because your Microsoft 365 integration is already active."
                : "If your company's IT or Security department manages Microsoft 365, copy this official single-use authorization link and send it to them via email or Slack."}
            </p>

            {/* Copyable Link Box */}
            <div className="space-y-1.5">
              <div className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-white/50" : "text-gray-500"}`}>
                Direct IT Admin Authorization Link
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  disabled={isConsented}
                  value={directLink}
                  className={`w-full px-3.5 py-2.5 text-xs font-mono rounded-xl border outline-none truncate transition ${
                    isConsented
                      ? isDark
                        ? "bg-[#100f17] border-white/5 text-white/40 cursor-not-allowed"
                        : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "bg-[#100f17] border-[#2e2c3b] text-white/90 focus:border-purple-500"
                      : "bg-gray-50 border-gray-200 text-gray-800 focus:border-purple-500"
                  }`}
                />
                <button
                  onClick={handleCopyLink}
                  disabled={isConsented}
                  className={`px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 shrink-0 transition ${
                    isConsented
                      ? isDark
                        ? "bg-white/5 text-white/30 border border-white/5 cursor-not-allowed"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : copied
                      ? "bg-emerald-500 text-white cursor-pointer"
                      : "bg-purple-600 hover:bg-purple-500 text-white shadow-md cursor-pointer"
                  }`}
                >
                  {isConsented ? (
                    <>
                      <Lock className="w-3.5 h-3.5" />
                      <span>Expired</span>
                    </>
                  ) : copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {isConsented ? (
              <div
                className={`p-3 rounded-xl text-[11px] flex items-start gap-2.5 border ${
                  isDark
                    ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300/90"
                    : "bg-emerald-50 border-emerald-200 text-emerald-800"
                }`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>
                  This company is successfully connected to Microsoft 365. The authorization link was single-use and has been securely expired to protect your organization.
                </span>
              </div>
            ) : (
              <div
                className={`p-3 rounded-xl text-[11px] flex items-start gap-2.5 border ${
                  isDark
                    ? "bg-[#100f17] border-blue-500/20 text-white/70"
                    : "bg-blue-50 border-blue-200 text-blue-900"
                }`}
              >
                <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                <span>
                  Once your IT administrator opens this link, logs in, and clicks <b>Accept</b>, Pitchster is activated for everyone in your organization.
                </span>
              </div>
            )}
          </div>

          {isConsented ? (
            <div
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-xs border ${
                isDark
                  ? "bg-white/5 text-white/40 border-white/5"
                  : "bg-gray-100 text-gray-400 border-gray-200"
              }`}
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Link Expired (Already Connected)</span>
            </div>
          ) : (
            <button
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-xs border transition cursor-pointer ${
                isDark
                  ? "bg-white/5 hover:bg-white/10 text-white border-white/10"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-200"
              }`}
            >
              <Copy className="w-3.5 h-3.5" />
              Copy Shareable IT Admin Link
            </button>
          )}
        </div>
      </div>

      {/* Enterprise Security & Transparency Card */}
      <div
        className={`rounded-2xl border transition-all p-6 space-y-4 ${
          isDark
            ? "bg-[#16151f] border-[#2e2c3b] text-white shadow-md shadow-black/20"
            : "bg-white border-gray-200 text-gray-900 shadow-sm"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <Lock className="w-4 h-4" />
          </div>
          <div>
            <h4 className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Permissions Requested & Enterprise Security
            </h4>
            <p className={`text-xs ${isDark ? "text-white/50" : "text-gray-500"} mt-0.5`}>
              Pitchster requests the minimum necessary permissions to detect meetings and provide pitch coaching.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <div
            className={`p-4 rounded-xl border ${
              isDark ? "bg-[#100f17] border-[#2e2c3b]" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-400" />
              <span className={`text-xs font-bold font-mono ${isDark ? "text-white" : "text-gray-800"}`}>
                Calendars.Read
              </span>
            </div>
            <p className={`text-[11px] ${isDark ? "text-white/60" : "text-gray-600"} leading-relaxed`}>
              Read-only calendar access. Pitchster reads upcoming meeting titles, times, and attendees to automatically sync scheduled pitches. <b>Pitchster never writes, modifies, or deletes calendar events.</b>
            </p>
          </div>

          <div
            className={`p-4 rounded-xl border ${
              isDark ? "bg-[#100f17] border-[#2e2c3b]" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-400" />
              <span className={`text-xs font-bold font-mono ${isDark ? "text-white" : "text-gray-800"}`}>
                User.Read
              </span>
            </div>
            <p className={`text-[11px] ${isDark ? "text-white/60" : "text-gray-600"} leading-relaxed`}>
              Reads basic profile data (display name and primary email address) to securely associate calendar sessions with the correct sales rep.
            </p>
          </div>

          <div
            className={`p-4 rounded-xl border ${
              isDark ? "bg-[#100f17] border-[#2e2c3b]" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className={`text-xs font-bold font-mono ${isDark ? "text-white" : "text-gray-800"}`}>
                offline_access
              </span>
            </div>
            <p className={`text-[11px] ${isDark ? "text-white/60" : "text-gray-600"} leading-relaxed`}>
              Enables secure background refresh tokens so employees do not need to sign into Microsoft repeatedly every time they open Pitchster.
            </p>
          </div>
        </div>
      </div>
    </Box>
  );
};
